import { config } from '@/app/config';
import { getSupabase } from '@/supabase/client';
import { createId, nowIso } from '@/domain/helpers';
import { getDb, getMeta, setMeta } from '@/storage/db';
import {
  extensionForImageMime,
  isSupportedImageFile,
  normalizeImageMime,
} from '@/platform/images';

export type WallpaperSource = 'none' | 'own' | 'partner';

export const WALLPAPER_SOURCE_KEY = 'wallpaper_source';
const WALLPAPER_BUCKET = 'wallpapers';
const MAX_WALLPAPER_BYTES = 6 * 1024 * 1024;

export function normalizeWallpaperSource(value: unknown): WallpaperSource {
  if (value === 'own' || value === 'partner' || value === 'none') return value;
  return 'none';
}

export async function getWallpaperSource(): Promise<WallpaperSource> {
  const value = await getMeta<WallpaperSource>(WALLPAPER_SOURCE_KEY);
  if (value === 'own' || value === 'partner' || value === 'none') return value;
  const legacyId = await getMeta<string>('bg_image_id');
  return legacyId ? 'own' : 'none';
}

export async function setWallpaperSourceMeta(source: WallpaperSource): Promise<void> {
  await setMeta(WALLPAPER_SOURCE_KEY, source);
}

/** Upload wallpaper; returns Storage path (or demo path key). */
export async function uploadSharedWallpaper(userId: string, file: File): Promise<string> {
  if (!isSupportedImageFile(file)) throw new Error('unsupported_image');
  if (file.size > MAX_WALLPAPER_BYTES) throw new Error('too_large');

  if (config.mode !== 'production') {
    const dataUrl = await readAsDataUrl(file);
    await setMeta(`wallpaper_demo_${userId}`, dataUrl);
    return `demo:${userId}`;
  }

  const supabase = getSupabase();
  if (!supabase) throw new Error('no_supabase');

  const mime = normalizeImageMime(file);
  const ext = extensionForImageMime(mime, file.name);
  const path = `${userId}/wallpaper.${ext}`;
  const { error } = await supabase.storage.from(WALLPAPER_BUCKET).upload(path, file, {
    upsert: true,
    contentType: mime,
    cacheControl: '3600',
  });
  if (error) throw error;

  const siblings = ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'gif', 'avif']
    .filter((e) => e !== ext)
    .map((e) => `${userId}/wallpaper.${e}`);
  if (siblings.length) await supabase.storage.from(WALLPAPER_BUCKET).remove(siblings);
  return path;
}

export async function removeSharedWallpaper(userId: string): Promise<void> {
  if (config.mode !== 'production') {
    await setMeta(`wallpaper_demo_${userId}`, null);
    return;
  }
  const supabase = getSupabase();
  if (!supabase) return;
  const names = ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'gif', 'avif'].map(
    (e) => `${userId}/wallpaper.${e}`,
  );
  await supabase.storage.from(WALLPAPER_BUCKET).remove(names);
}

export function resolveOwnWallpaperPublicUrl(storagePath: string | null | undefined): string | null {
  if (!storagePath) return null;
  if (storagePath.startsWith('demo:')) return null;
  if (storagePath.startsWith('data:')) return storagePath;
  if (config.mode !== 'production') return null;
  const supabase = getSupabase();
  if (!supabase) return null;
  const { data } = supabase.storage.from(WALLPAPER_BUCKET).getPublicUrl(storagePath);
  return data.publicUrl || null;
}

/** Download cloud wallpaper into Dexie cache; returns local blob id + object URL. */
export async function cacheWallpaperFromStorage(
  storagePath: string,
): Promise<{ id: string; url: string } | null> {
  if (!storagePath) return null;

  if (storagePath.startsWith('demo:')) {
    const userId = storagePath.slice('demo:'.length);
    const dataUrl = await getMeta<string>(`wallpaper_demo_${userId}`);
    if (!dataUrl) return null;
    const blob = await (await fetch(dataUrl)).blob();
    return putLocalWallpaperBlob(blob);
  }

  if (config.mode !== 'production') return null;
  const supabase = getSupabase();
  if (!supabase) return null;
  const { data, error } = await supabase.storage.from(WALLPAPER_BUCKET).download(storagePath);
  if (error || !data) {
    const publicUrl = resolveOwnWallpaperPublicUrl(storagePath);
    if (!publicUrl) return null;
    try {
      const res = await fetch(publicUrl);
      if (!res.ok) return null;
      const blob = await res.blob();
      return putLocalWallpaperBlob(blob);
    } catch {
      return null;
    }
  }
  return putLocalWallpaperBlob(data);
}

async function putLocalWallpaperBlob(blob: Blob): Promise<{ id: string; url: string }> {
  const db = getDb();
  const existingId = await getMeta<string>('bg_image_id');
  if (existingId) await db.backgrounds.delete(existingId);
  const id = createId();
  await db.backgrounds.put({
    id,
    blob,
    mimeType: blob.type || 'image/jpeg',
    createdAt: nowIso(),
  });
  await setMeta('bg_image_id', id);
  return { id, url: URL.createObjectURL(blob) };
}

export async function resolvePartnerWallpaperUrl(
  partnerId: string,
  cacheKey?: string,
): Promise<string | null> {
  if (config.mode !== 'production') {
    const dataUrl = await getMeta<string>(`wallpaper_demo_${partnerId}`);
    return dataUrl || null;
  }

  const supabase = getSupabase();
  if (!supabase) return null;

  const { data: listed } = await supabase.storage.from(WALLPAPER_BUCKET).list(partnerId, {
    limit: 20,
  });
  const candidates: string[] = [];
  if (listed?.length) {
    for (const file of listed) {
      if (file.name?.startsWith('wallpaper.')) {
        candidates.push(`${partnerId}/${file.name}`);
      }
    }
  }
  if (!candidates.length) {
    for (const ext of ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'gif', 'avif']) {
      candidates.push(`${partnerId}/wallpaper.${ext}`);
    }
  }

  for (const path of candidates) {
    const { data } = supabase.storage.from(WALLPAPER_BUCKET).getPublicUrl(path);
    if (!data.publicUrl) continue;
    const ok = await probeImageUrl(data.publicUrl);
    if (ok) return withCache(data.publicUrl, cacheKey);
  }
  return null;
}

function probeImageUrl(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

function withCache(url: string | null, cacheKey?: string): string | null {
  if (!url) return null;
  if (!cacheKey) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${encodeURIComponent(cacheKey)}`;
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('read_failed'));
    reader.readAsDataURL(file);
  });
}

export { MAX_WALLPAPER_BYTES };
